
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Star } from "lucide-react";
import { Dispatch, SetStateAction, useContext } from "react";
import { useTranslation } from "react-i18next";
import { IndexContext, OverlayContext } from "./CtaOverlay.component";
import { StarsBox } from "./StarsBox.component";
import { useTimer } from "../../hooks";
import { useRatingQuery } from "../../queries";
import { useAuthStore } from "../../stores";
import { getLearningObjectType } from "../../utils/general";

type IRating = {
  setActiveIndex: Dispatch<SetStateAction<number | undefined>>
  iconOnly?: boolean
  keepIconsInMobile?: boolean
  addOffsetPosition?: boolean
}

export const Rating = ({ 
  setActiveIndex, 
  iconOnly = false, 
  keepIconsInMobile = false, 
  addOffsetPosition = false
}: IRating) => {
  const { t } = useTranslation();
  const {
    id,
    learningObjectTypology
  } = useContext(OverlayContext);
  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down("sm"));
  const activeIndex = useContext(IndexContext);
  
  const { startTimer, stopTimer, isActive } = useTimer(500, false);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
    
  const learningObjectType = getLearningObjectType(learningObjectTypology);
  const {
    data: ratingStatistic
  } = useRatingQuery({
    enabled: Boolean(id),
    learningObjectId: id ?? "",
    learningObjectTypology:learningObjectType
  });
  
  const loStatistic = {
    averageRating: ratingStatistic?.objectStatistics.averageRating || 0,
    userFavourite: Boolean(ratingStatistic?.userRating.isFavouriteForLearner &&
      ratingStatistic?.objectStatistics?.learningObjectId),
    userRating: ratingStatistic?.userRating?.rating || 0
  };

  return (
    <Box
      className="button"
      id="button-valutazione"
      tabIndex={ 0 }
      position={ "relative" }
      aria-current={ activeIndex === 1 }
      aria-labelledby="overlay-rating-1-to-5"
      aria-label={ t("rating_btn_description") }
      aria-controls="item-2a"
      onFocus={ () => { 
        setActiveIndex(1); 
        stopTimer();
      } }

      onBlur={ () => {
        setActiveIndex(undefined);
        startTimer();
      } }
      onMouseEnter={ ()=> {
        setActiveIndex(1);
        stopTimer();
      } }
      onMouseLeave={ ()=> {
        setActiveIndex(undefined);
        startTimer();
      } }
      onKeyDown={ (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setActiveIndex((prev:number|undefined) => (prev === 1 ? undefined : 1));
          startTimer();
        }
      } }

      sx={ {
        gap: "0.5rem",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "1rem 0.75rem"
      } }
    >
    
      <StarsBox 
        id={ id ?? "" }
        userRating={ loStatistic.userRating }
        learningObjectTypology={ learningObjectTypology }
        visibility={ isActive }
        maxRating = { smartConfiguration?.ratingMax || 1 }
        setActiveIndex={ setActiveIndex }
        stopTimer={ stopTimer }
        startTimer={ startTimer }
        keepIconsInMobile={ keepIconsInMobile }
        addOffsetPosition={ addOffsetPosition }
      />

      <Star size={ "1rem" } />
      { !isMedium && !iconOnly && (
        <Typography fontSize={ "0.875rem" }>
          { t("rating") }
        </Typography>
      ) }
    </Box>
  );
};
